import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import { ToolComposingIndicator } from "../components/ToolComposingIndicator";
import { useToolComposingFeedback } from "../hooks/useToolComposingFeedback";
import {
  buildIdaChatStreamConfig,
  subscribeToConversationSseFrames,
} from "../utils/idaChatStreamConfig";
import {
  ChatProvider,
  useChat,
  useChatStore,
  useLoadConversation,
} from "@metalmind-ab/mmagent-chat-ui";

import { useChatProviderGeneration } from "../hooks/useChatProviderGeneration";
import { useWorkspaceOnboardingChatSelection } from "../hooks/useWorkspaceOnboardingChatSelection";

type PendingStream = {
  enqueue: (chunk: Uint8Array) => void;
  signal: AbortSignal | null;
};

const originalFetch = global.fetch;
const originalWindowFetch = window.fetch;

function responseWithBody(
  body: { getReader: () => unknown },
  headers = new Headers(),
): Response {
  return {
    ok: true,
    status: 200,
    headers,
    body,
  } as Response;
}

function Controls({
  resetProvider,
  guard,
  observeFrames = false,
  restoreSetupChat = false,
}: {
  resetProvider: ReturnType<typeof useChatProviderGeneration>["reset"];
  guard: ReturnType<
    typeof useChatProviderGeneration
  >["freshChatConversationGuardRef"]["current"];
  observeFrames?: boolean;
  restoreSetupChat?: boolean;
}) {
  const { sendMessage, cancelStream } = useChat();
  const { startNewConversation } = useLoadConversation();
  const conversationId = useChatStore((state) => state.conversationId);
  useWorkspaceOnboardingChatSelection({
    enabled: restoreSetupChat,
    workspaceId: "workspace-1",
    conversations: [
      {
        id: 8840,
        kind: "chat",
        createdAt: "2026-10-26T00:00:00Z",
      },
    ],
    currentId: conversationId,
    inventoryLoaded: restoreSetupChat,
    isLoading: false,
    select: (id) => useChatStore.getState().setConversationId(String(id)),
  });
  const { label } = useToolComposingFeedback(
    observeFrames ? conversationId : null,
  );

  return (
    <>
      <button
        onClick={() => {
          resetProvider(
            useChatStore.getState().conversationId,
            cancelStream,
            () => useChatStore.getState().setLoading(false),
          );
          startNewConversation();
        }}
      >
        New Chat
      </button>
      <button
        onClick={() => {
          guard.prepareSend(useChatStore.getState().conversationId, () =>
            useChatStore.getState().setConversationId(null),
          );
          void sendMessage("hello");
        }}
      >
        Send
      </button>
      {label ? <ToolComposingIndicator label={label} /> : null}
    </>
  );
}

function ChatProviderHarness({
  children,
  observeFrames = false,
  restoreSetupChat = false,
}: {
  children?: React.ReactNode;
  observeFrames?: boolean;
  restoreSetupChat?: boolean;
}) {
  const { providerKey, reset, freshChatConversationGuardRef } =
    useChatProviderGeneration("workspace-1");
  const streamConfig = observeFrames
    ? buildIdaChatStreamConfig("https://example.test/api/workspace/ida")
    : {};
  if (observeFrames) {
    // The browser shares these globals; align them in jsdom after installing
    // the fetch observer so ChatProvider's POST uses the observed fetch.
    global.fetch = window.fetch;
  }
  return (
    <ChatProvider
      key={providerKey}
      config={{
        apiBaseUrl: "https://example.test/api/workspace/ida",
        ...streamConfig,
        onSubmissionId: (_submissionId, conversationId) =>
          useChatStore.getState().setConversationId(conversationId),
        enableConversationSubscription: true,
        maxReconnectAttempts: 1,
        reconnectBackoffMs: 0,
      }}
    >
      <Controls
        resetProvider={reset}
        guard={freshChatConversationGuardRef.current}
        observeFrames={observeFrames}
        restoreSetupChat={restoreSetupChat}
      />
      {children}
    </ChatProvider>
  );
}

describe("New Chat stream isolation", () => {
  afterEach(() => {
    global.fetch = originalFetch;
    window.fetch = originalWindowFetch;
    act(() => {
      useChatStore.getState().clearMessages();
      useChatStore.getState().setConversationId(null);
      useChatStore.getState().setLoading(false);
    });
  });

  it("sends a typed message to the setup conversation restored after reload", async () => {
    const postedUrls: string[] = [];
    const encoder = new TextEncoder();
    global.fetch = jest.fn(async (input, init) => {
      const url = String(input);
      if (init?.method === "GET") {
        const body = new ReadableStream<Uint8Array>({
          start(controller) {
            controller.close();
          },
        });
        return responseWithBody(body);
      }
      postedUrls.push(url);
      const body = new ReadableStream<Uint8Array>({
        start(controller) {
          controller.enqueue(encoder.encode('data: {"type":"done"}\n\n'));
          controller.close();
        },
      });
      return responseWithBody(body);
    }) as typeof fetch;

    render(<ChatProviderHarness restoreSetupChat />);
    await waitFor(() =>
      expect(useChatStore.getState().conversationId).toBe("8840"),
    );
    fireEvent.click(screen.getByRole("button", { name: "Send" }));

    await waitFor(() => expect(postedUrls).toHaveLength(1));
    expect(postedUrls[0]).toBe(
      "https://example.test/api/workspace/ida/chat/conversations/8840/messages/stream",
    );
  });

  it("aborts the old passive stream and sends the next message without its id", async () => {
    const streams: PendingStream[] = [];
    const postedUrls: string[] = [];
    const encoder = new TextEncoder();
    global.fetch = jest.fn(async (input, init) => {
      const url = String(input);
      if (init?.method === "GET") {
        let resolveRead!: (
          result: ReadableStreamReadResult<Uint8Array>,
        ) => void;
        const body = {
          getReader: () => ({
            read: () =>
              new Promise<ReadableStreamReadResult<Uint8Array>>((resolve) => {
                resolveRead = resolve;
              }),
            cancel: () => {
              resolveRead({ done: true, value: undefined });
              return Promise.resolve();
            },
          }),
        };
        const signal = (init.signal as AbortSignal | null) ?? null;
        signal?.addEventListener(
          "abort",
          () => resolveRead({ done: true, value: undefined }),
          { once: true },
        );
        streams.push({
          enqueue: (chunk) => resolveRead({ done: false, value: chunk }),
          signal,
        });
        return responseWithBody(body);
      }

      postedUrls.push(url);
      const body = new ReadableStream<Uint8Array>({
        start(controller) {
          controller.enqueue(encoder.encode('data: {"type":"done"}\n\n'));
          controller.close();
        },
      });
      return responseWithBody(body);
    }) as typeof fetch;

    useChatStore.getState().setConversationId("8840");
    render(<ChatProviderHarness />);
    await waitFor(() => expect(streams).toHaveLength(1));

    fireEvent.click(screen.getByRole("button", { name: "New Chat" }));
    expect(useChatStore.getState().conversationId).toBeNull();
    expect(streams[0].signal?.aborted).toBe(true);

    // Deliver a frame from the old connection after New Chat has rendered.
    streams[0].enqueue(
      encoder.encode(
        'data: {"type":"content_delta","conversation_id":"8840","content":"stale"}\n\n',
      ),
    );
    await waitFor(() =>
      expect(useChatStore.getState().conversationId).toBeNull(),
    );
    fireEvent.click(screen.getByRole("button", { name: "Send" }));

    await waitFor(() => expect(postedUrls).toHaveLength(1));
    expect(postedUrls[0]).toBe(
      "https://example.test/api/workspace/ida/chat/messages/stream",
    );
  });

  it("keeps the provider mounted when New Chat aborts a passive stream", async () => {
    const streams: PendingStream[] = [];
    global.fetch = jest.fn(async (input, init) => {
      if (init?.method === "GET") {
        let resolveRead:
          ((result: ReadableStreamReadResult<Uint8Array>) => void) | undefined;
        const body = {
          getReader: () => ({
            read: () =>
              new Promise<ReadableStreamReadResult<Uint8Array>>((resolve) => {
                resolveRead = resolve;
              }),
            cancel: () => {
              resolveRead?.({ done: true, value: undefined });
              return Promise.resolve();
            },
          }),
        };
        const signal = (init.signal as AbortSignal | null) ?? null;
        signal?.addEventListener(
          "abort",
          () => resolveRead?.({ done: true, value: undefined }),
          { once: true },
        );
        streams.push({
          enqueue: jest.fn(),
          signal,
        });
        return responseWithBody(body);
      }
      throw new Error(`Unexpected request: ${String(input)}`);
    }) as typeof fetch;

    useChatStore.getState().setConversationId("8840");
    const { rerender } = render(<ChatProviderHarness />);
    await waitFor(() => expect(streams).toHaveLength(1));
    const originalSignal = streams[0].signal;

    fireEvent.click(screen.getByRole("button", { name: "New Chat" }));
    rerender(<ChatProviderHarness />);

    expect(streams[0].signal).toBe(originalSignal);
    expect(streams[0].signal?.aborted).toBe(true);
    expect(streams).toHaveLength(1);
  });

  it("shows a first-message composing frame scoped by the response conversation id", async () => {
    const streams: PendingStream[] = [];
    const encoder = new TextEncoder();
    const observedFrames: unknown[] = [];
    const unsubscribe = subscribeToConversationSseFrames((frame) =>
      observedFrames.push(frame),
    );
    let finishPostStream!: () => void;
    const fetchMock = jest.fn(async (_input, init) => {
      if (init?.method === "GET") {
        let resolveRead!: (
          result: ReadableStreamReadResult<Uint8Array>,
        ) => void;
        const body = {
          getReader: () => ({
            read: () =>
              new Promise<ReadableStreamReadResult<Uint8Array>>((resolve) => {
                resolveRead = resolve;
              }),
            cancel: () => {
              resolveRead({ done: true, value: undefined });
              return Promise.resolve();
            },
          }),
        };
        const signal = (init.signal as AbortSignal | null) ?? null;
        signal?.addEventListener(
          "abort",
          () => resolveRead({ done: true, value: undefined }),
          { once: true },
        );
        streams.push({ enqueue: () => {}, signal });
        return responseWithBody(body);
      }

      const finishers: Array<() => void> = [];
      const makePostResponse = (): Response => {
        const body = new ReadableStream<Uint8Array>({
          start(controller) {
            controller.enqueue(
              encoder.encode(
                'data: {"type":"tool_composing","call_id":"new-call","tool_name":"generate_document","label":"Writing document"}\n\n',
              ),
            );
            finishers.push(() => {
              controller.enqueue(
                encoder.encode(
                  'data: {"type":"tool_calling","tool_call_id":"new-call"}\n\n' +
                    'data: {"type":"done"}\n\n',
                ),
              );
              controller.close();
            });
          },
        });
        return responseWithBody(
          body,
          new Headers({
            "X-Chat-Conversation-ID": "new-42",
            "X-Chat-Submission-ID": "submission-new",
          }),
        );
      };
      const response = makePostResponse();
      response.clone = () => makePostResponse();
      finishPostStream = () => finishers.forEach((finish) => finish());
      return response;
    }) as typeof fetch;
    global.fetch = fetchMock;
    window.fetch = fetchMock;
    useChatStore.getState().setConversationId("8840");
    render(<ChatProviderHarness observeFrames />);
    await waitFor(() => expect(streams).toHaveLength(1));

    fireEvent.click(screen.getByRole("button", { name: "New Chat" }));
    fireEvent.click(screen.getByRole("button", { name: "Send" }));

    await waitFor(() =>
      expect(fetchMock).toHaveBeenCalledWith(
        "https://example.test/api/workspace/ida/chat/messages/stream",
        expect.objectContaining({ method: "POST" }),
      ),
    );
    expect(
      await screen.findByText("Preparing: Writing document…"),
    ).toBeTruthy();
    expect(observedFrames).toContainEqual(
      expect.objectContaining({
        type: "tool_composing",
        conversation_id: "new-42",
        call_id: "new-call",
      }),
    );

    act(() => finishPostStream());
    await waitFor(() =>
      expect(screen.queryByText("Preparing: Writing document…")).toBeNull(),
    );
    unsubscribe();
  });

  it("does not publish first-message frames without a response conversation id", async () => {
    const encoder = new TextEncoder();
    const observedFrames: unknown[] = [];
    const unsubscribe = subscribeToConversationSseFrames((frame) =>
      observedFrames.push(frame),
    );
    const fetchMock = jest.fn(async (_input, init) => {
      if (init?.method === "GET") {
        let resolveRead:
          ((result: ReadableStreamReadResult<Uint8Array>) => void) | undefined;
        const body = {
          getReader: () => ({
            read: () =>
              new Promise<ReadableStreamReadResult<Uint8Array>>((resolve) => {
                resolveRead = resolve;
              }),
            cancel: () => {
              resolveRead?.({ done: true, value: undefined });
              return Promise.resolve();
            },
          }),
        };
        const signal = (init.signal as AbortSignal | null) ?? null;
        signal?.addEventListener(
          "abort",
          () => resolveRead?.({ done: true, value: undefined }),
          { once: true },
        );
        return responseWithBody(body);
      }
      const body = new ReadableStream<Uint8Array>({
        start(controller) {
          controller.enqueue(
            encoder.encode(
              'data: {"type":"tool_composing","call_id":"unscoped-call","tool_name":"generate_document","label":"Writing document"}\n\n' +
                'data: {"type":"done"}\n\n',
            ),
          );
          controller.close();
        },
      });
      return responseWithBody(body);
    }) as typeof fetch;
    global.fetch = fetchMock;
    window.fetch = fetchMock;
    useChatStore.getState().setConversationId("8840");
    render(<ChatProviderHarness observeFrames />);
    await waitFor(() => expect(fetchMock).toHaveBeenCalled());

    fireEvent.click(screen.getByRole("button", { name: "New Chat" }));
    fireEvent.click(screen.getByRole("button", { name: "Send" }));

    await waitFor(() =>
      expect(fetchMock).toHaveBeenCalledWith(
        "https://example.test/api/workspace/ida/chat/messages/stream",
        expect.objectContaining({ method: "POST" }),
      ),
    );
    await act(async () => new Promise((resolve) => setTimeout(resolve, 0)));
    expect(observedFrames).toEqual([]);
    expect(screen.queryByText(/Preparing:/)).toBeNull();
    unsubscribe();
  });
});
